import { Channel } from "../Com/Com.Interfaces";
import { LocationInterface } from "../Locations/Location.Interface";
import { EntityWithCreated } from "../Shared/EntityWithCreated.Interface";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { CountryCode } from "../UnitsAndLocalization/CountryCode";
import { LanguageCode } from "../UnitsAndLocalization/Languages.Interface";
import { UserPropKey } from "../Variables/Generated/UserPropKey.Data";
import { UserVarInterface } from "../Variables/UserVariables.Interface";

//LINK - libs/entities/src/User/User.Entity.ts

// ─── Messaging consent (source of truth) ─────────────────────────────────────
// Per-channel/category. Category-capable (ships with a single "general" category),
// opt-in by default, independent per channel.
// See docs/snowy/plans/2026-06-16-message-delivery-status-seen-and-optout.md and, for the gate that
// enforces it on every send, docs/snowy/plans/2026-09-22-outbound-guarantee.md (decision 7).
export type MessagingConsentChannel = "sms" | "voice" | "email";
/**
 * What a message is for. `general` is every account message (sequence reminders, replies, ticket
 * updates, codes); `marketing` is anything selling or re-engaging (campaigns, starters, abandoned
 * cart). STOP sets `general: out`, which silences both; a `marketing: out` silences marketing only.
 */
export type MessagingConsentCategory = "general" | "marketing";
export const MESSAGING_CONSENT_CATEGORIES: readonly MessagingConsentCategory[] = ["general", "marketing"];
export type MessagingConsentStatus = "in" | "out";
export type MessagingConsentSource = "default" | "stop" | "start" | "manual" | "fms" | "migration";

export interface MessagingConsentEntry {
  status: MessagingConsentStatus;
  /** ISO timestamp when this status was set. */
  at?: string;
  source?: MessagingConsentSource;
}

export type MessagingConsent = {
  [channel in MessagingConsentChannel]?: {
    [category in MessagingConsentCategory]?: MessagingConsentEntry;
  };
};

export interface UserInterface extends OrgBaseInterface, EntityWithCreated {
  name: string;
  firstName?: string;
  lastName?: string;
  number?: number;
  lastInteractionAt: Date; // This is a message from the user or from an agent / bot
  lastUserMessageAt?: Date; // this is ONLY the last message from the user, not from an agent or bot.
  lastUserMessageChannel?: Channel; // The channel of the last user message
  lastUserMessage?: string; // The last message sent by the user
  /** When the Sequences reconciler last recomputed this user's memberships (live wake or backfill); stamped in the reconcile transaction. */
  lastReconcilerRun?: Date;

  smsOptOut?: boolean;
  /** Per-channel/category messaging consent (source of truth). Opt-in by default. See MessagingConsent. */
  messagingConsent?: MessagingConsent;
  isArchived?: boolean;
  language?: LanguageCode;
  email?: string;
  emails?: string[];

  phoneNumber?: string;
  phoneNumbers?: string[];

  addressLine1?: string;
  addressLine2?: string;
  state?: string;
  city?: string;
  postalCode?: string;
  countryCode?: CountryCode;

  locationId?: number; // This should not have an array version, as it is a single location. If we want a list of locations, we should use the leases and get the locations from that.
  locationIds?: number[];
  location?: LocationInterface;

  unitId?: number;
  unitIds?: number[];
  leaseId?: number;
  leaseIds?: number[];
  tenantId?: number;

  /**
   * USER-LEVEL "last focus" — where this caller MOST RECENTLY focused, persisted
   * ACROSS conversations/calls. NOT the same as `BotConversation.focusLocationId`
   * (the per-conversation, in-session focus from the M0 move). Seeds a new
   * conversation's focus via `resolveFocus` so a returning voice caller inherits
   * where they last were. Renamed from `focusLocationId` to mark the semantics.
   */
  lastFocusLocationId?: number;
  /** USER-LEVEL last focus — unit most recently focused, persisted across conversations. See `lastFocusLocationId`. */
  lastFocusUnitId?: number;
  /** USER-LEVEL last focus — lease most recently focused, persisted across conversations. See `lastFocusLocationId`. */
  lastFocusLeaseId?: number;
  tenantIds?: number[];
  defaultGroupId?: number;
  limitToGroupId?: number; // This is a group that the user is limited to, for example, if they are a tenant, they should only be able to see their own group. This is used for store lookups and other operations where we want to limit the user to a specific group.
  // Not on User.Interface.ts
  token?: string;
  variables?: UserVarInterface[];

  channels: Channel[]; // This is a list of channels the user has interacted with

  version: number;
}

export enum NameType {
  FIRST_NAME = "first",
  LAST_NAME = "last",
  FULL_NAME = "full"
}

export enum UserMapValueType {
  NUMBER = "number",
  STRING = "string",
  BOOLEAN = "boolean",
  DATE = "date",
  STRING_ARRAY = "string array",
  ID_ARRAY = "id array"
}
export interface UserMapValue {
  type: UserMapValueType;
  name: string;
}
type UserInterfaceUserPropKeyMapType = {
  [key in UserPropKey]: UserMapValue;
};
export const UserInterfaceUserPropKeyMap: UserInterfaceUserPropKeyMapType = {
  [UserPropKey.Id]: { type: UserMapValueType.NUMBER, name: "id" },

  [UserPropKey.Name]: { type: UserMapValueType.STRING, name: "name" },
  [UserPropKey.FirstName]: { type: UserMapValueType.STRING, name: "firstName" },
  [UserPropKey.LastName]: { type: UserMapValueType.STRING, name: "lastName" },
  [UserPropKey.Number]: { type: UserMapValueType.NUMBER, name: "number" },
  // [UserPropKey.LastInteraction]: { type: UserMapValueType.DATE, name: "lastInteractionAt" },
  [UserPropKey.SMSOptOut]: { type: UserMapValueType.BOOLEAN, name: "smsOptOut" },
  [UserPropKey.IsArchived]: { type: UserMapValueType.BOOLEAN, name: "isArchived" },
  [UserPropKey.Language]: { type: UserMapValueType.STRING, name: "language" },
  [UserPropKey.Email]: { type: UserMapValueType.STRING, name: "email" },
  [UserPropKey.Emails]: { type: UserMapValueType.STRING_ARRAY, name: "emails" },

  [UserPropKey.PhoneNumber]: { type: UserMapValueType.STRING, name: "phoneNumber" },
  [UserPropKey.PhoneNumbers]: { type: UserMapValueType.STRING_ARRAY, name: "phoneNumbers" },

  [UserPropKey.AddressLine1]: { type: UserMapValueType.STRING, name: "addressLine1" },
  [UserPropKey.AddressLine2]: { type: UserMapValueType.STRING, name: "addressLine2" },
  [UserPropKey.State]: { type: UserMapValueType.STRING, name: "state" },
  [UserPropKey.City]: { type: UserMapValueType.STRING, name: "city" },
  [UserPropKey.PostalCode]: { type: UserMapValueType.STRING, name: "postalCode" },
  [UserPropKey.CountryCode]: { type: UserMapValueType.STRING, name: "countryCode" },

  [UserPropKey.LocationId]: { type: UserMapValueType.NUMBER, name: "locationId" },
  [UserPropKey.LocationIds]: { type: UserMapValueType.ID_ARRAY, name: "locationIds" },
  [UserPropKey.UnitId]: { type: UserMapValueType.NUMBER, name: "unitId" },
  [UserPropKey.UnitIds]: { type: UserMapValueType.ID_ARRAY, name: "unitIds" },
  [UserPropKey.LeaseId]: { type: UserMapValueType.NUMBER, name: "leaseId" },
  [UserPropKey.LeaseIds]: { type: UserMapValueType.ID_ARRAY, name: "leaseIds" },
  [UserPropKey.TenantId]: { type: UserMapValueType.NUMBER, name: "tenantId" },
  [UserPropKey.TenantIds]: { type: UserMapValueType.ID_ARRAY, name: "tenantIds" },

  [UserPropKey.DefaultGroupId]: { type: UserMapValueType.NUMBER, name: "defaultGroupId" },
  [UserPropKey.LimitToGroupId]: { type: UserMapValueType.NUMBER, name: "limitToGroupId" },
  [UserPropKey.LastInteraction]: { type: UserMapValueType.DATE, name: "lastInteractionAt" }
};

export interface UserAddress {
  origin?: string;
  port?: string;
  protocol?: string;
  hostname?: string;
  pathname?: string;
  hash?: string;
  isMobile?: boolean;
}

export interface GetOrCreateUserInterface {
  user: UserInterface;
  isNew: boolean;
}

export interface ChannelPresence {
  channel: string;
  lastSeenMs: number;
  windowMs: number;
  online: boolean;
}

export interface OnlineRosterEntry {
  userId: number;
  lastSeenMs: number; // freshest across channels
  online: boolean; // true if any channel online
  channels: ChannelPresence[];
}
export const UserOnlineDefaultWindowMs = 1 * 60 * 1000; // 1h
export const UserOnlineWindowByChannel: Record<string, number> = {
  [Channel.WEB]: 15 * 1000, // 30 sec
  [Channel.VOICE]: 60 * 1000, // 60 sec
  [Channel.SMS]: 5 * 60 * 1000, // 5 min
  [Channel.GOOGLE_MY_BUSINESS]: 5 * 60 * 1000, // 10 min
  [Channel.FACEBOOK_MESSENGER]: 5 * 60 * 1000, // 5 min
  [Channel.WHATSAPP]: 5 * 60 * 1000 // 5 min
};
